import React, { useMemo, useState, memo, useRef, useCallback } from 'react';
import { styled } from '@compiled/react';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import type { StateDependencyItem } from '@atlassian/jira-aais-dependency-lines-overlay/src/common/ui/types.tsx';
import { SELECTED_ISSUE_PARAM } from '@atlassian/jira-business-constants/src/index.tsx';
import { BusinessDocumentTitle } from '@atlassian/jira-business-entity-common/src/utils/document-title/index.tsx';
import { useIssueTypesAndFields } from '@atlassian/jira-business-entity-project/src/services/issue-types-and-fields/index.tsx';
import { useExpandCollapseAllItems } from '@atlassian/jira-business-expand-collapse-all-items-controller/src/index.tsx';
import {
	ExperienceSuccess,
	ExperienceFailed,
} from '@atlassian/jira-business-experience-tracking/src/controllers/experience-tracker/index.tsx';
import { useFilteredViewContextInput } from '@atlassian/jira-business-filters/src/controllers/default-query-input/index.tsx';
import { PrefetchIssueDataController } from '@atlassian/jira-business-issue-view-critical-data-loader/src/ui/index.tsx';
import ModalIssueApp from '@atlassian/jira-business-issue-view/src/ui/modal-issue-app/index.tsx';
import { SidebarIssueView } from '@atlassian/jira-business-issue-view/src/ui/sidebar-issue-app/index.tsx';
import { ChildrenController } from '@atlassian/jira-business-list-handlers/src/controllers/children/index.tsx';
import type { ChildrenQueryData } from '@atlassian/jira-business-list-handlers/src/controllers/children/types.tsx';
import { LoadingMoreSpinner } from '@atlassian/jira-business-load-more-spinner/src/index.tsx';
import { PerformanceAnalytics } from '@atlassian/jira-business-performance/src/ui/index.tsx';
import { RenderStartMark } from '@atlassian/jira-business-performance/src/ui/page-load/index.tsx';
import { JWMSpaStatePageReady } from '@atlassian/jira-business-spa-state-page-ready/src/ui/index.tsx';
import { BUSINESS_TIMELINE } from '@atlassian/jira-common-constants/src/analytics-sources.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { useViewMode } from '@atlassian/jira-issue-context-service/src/main.tsx';
import { useStartDateField } from '@atlassian/jira-router-resources-business-configuration/src/index.tsx';
import { toIssueKey } from '@atlassian/jira-shared-types/src/general.tsx';
import type { OnChartItemUpdate } from '@atlassian/jira-software-roadmap-timeline-table-kit/src/common/types/chart-item.tsx';
import type { InteractiveChartItem } from '@atlassian/jira-software-roadmap-timeline-table-kit/src/controllers/table-providers/chart-item-interaction/common/types.tsx';
import { RoadmapListItemContentLite } from '@atlassian/jira-software-roadmap-timeline-table-kit/src/ui/list-item-content-lite/index.tsx';
import {
	DEFAULT_LIST_WIDTH,
	INSIDE,
	DEFAULT_HEADER_ROW_ID,
	zIndex,
} from '@atlassian/jira-software-roadmap-timeline-table/src/common/constants.tsx';
import { useHeaderState } from '@atlassian/jira-software-roadmap-timeline-table/src/common/context/header/index.tsx';
import type {
	OnItemExpandChanged,
	OnCreateClick,
	OnItemDrop,
} from '@atlassian/jira-software-roadmap-timeline-table/src/common/types/callbacks.tsx';
import type { TimelineTableInteractionMetricType } from '@atlassian/jira-software-roadmap-timeline-table/src/common/types/interaction-metrics.tsx';
import type { Item } from '@atlassian/jira-software-roadmap-timeline-table/src/common/types/item.tsx';
import type {
	ListItemContentProps as ListItemContentRendererProps,
	ListItemCreateProps as ListItemCreateRendererProps,
	ChartItemContentProps as ChartItemContentRendererProps,
	ChartItemContentState as ChartItemContentRendererState,
	ListItemMenuOptionProps,
	ListItemModalProps,
} from '@atlassian/jira-software-roadmap-timeline-table/src/renderers/index.tsx';
import Table from '@atlassian/jira-software-roadmap-timeline-table/src/view.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { useQueryParam } from '@atlassian/react-resource-router';
import { MAIN_EXPERIENCE } from '../../common/constants.tsx';
import { LoadingItem } from '../../common/ui/loading/index.tsx';
import { DndLongTaskMetric } from '../../common/ui/metrics/dnd-metrics/index.tsx';
import { InteractionMetrics } from '../../common/ui/metrics/interaction-metrics/index.tsx';
import { getTimelineTableItemsHeight } from '../../common/utils/get-items-height/index.tsx';
import {
	useInteractionMetrics,
	EXPAND_CHANGED,
	ADD_ITEM_CLICKED,
} from '../../common/utils/interaction-metrics.tsx';
import { isItemSkeleton, LOADING_ITEMS } from '../../common/utils/item-skeleton.tsx';
import { TimelineViewChildQueryController } from '../../controllers/children-query/index.tsx';
import { useCreateItemAnchor } from '../../controllers/create-item-anchor/index.tsx';
import { useDependencyUpdater } from '../../controllers/dependency-updater/index.tsx';
import { ExpandCollapseAllController } from '../../controllers/expand-collapse-all/index.tsx';
import { useTimelineFeatures } from '../../controllers/features-context/index.tsx';
import { useHierarchyStore } from '../../controllers/hierarchy/index.tsx';
import { useTimelineUI } from '../../controllers/timeline-ui/index.tsx';
import {
	TimelineViewUpdater,
	TimelineViewUpdaterProvider,
} from '../../controllers/timeline-updater/index.tsx';
import { useTimelineViewIssues } from '../../controllers/timeline-view-issues/index.tsx';
import { useUpdateItem } from '../../controllers/update-item/index.tsx';
import { useUpdateItem as useJiraUpdateItem } from '../../controllers/update-item/jira-update-item/index.tsx';
import type { OnJiraItemUpdate } from '../../controllers/update-item/types.tsx';
import { useIntersectionObserver } from '../../controllers/use-intersection-observer/index.tsx';
import { useListItemMenuOptions } from '../../controllers/use-list-item-menu-options/index.tsx';
import type {
	TimelineViewIssue,
	TimelineViewIssuesReturn,
} from '../../services/get-timeline-view-issues/types.tsx';
import { ChartItemContentLite } from './chart-item-content-lite/index.tsx';
import { ChartItemContentSkeleton } from './chart-item-content-skeleton/index.tsx';
import { ChartItemContent } from './chart-item-content/index.tsx';
import { ChartOverlay } from './chart-overlay/index.tsx';
import { ChartTimelineOverlay } from './chart-timeline-overlay/index.tsx';
import { timelineProps } from './constants.tsx';
import { marshalOptions } from './dependencies/marshal/index.tsx';
import { Export } from './export/index.tsx';
import { ListHeader } from './list-header/index.tsx';
import { ListItemContent } from './list-item-content/index.tsx';
import ListItemMenuOption from './list-item-menu-option/index.tsx';
import ListItemModal from './list-item-modal/index.tsx';
import messages from './messages.tsx';
import { StickyInlineCreate } from './sticky-inline-create/index.tsx';
import { StickyInlineNudge } from './sticky-inline-nudge/index.tsx';
import { TableProvider } from './table-provider/index.tsx';
import { TimelineTableInlineCreate } from './timeline-table-inline-create/index.tsx';
import Toolbar from './toolbar/index.tsx';

const getTimelineRenderers = ({
	childrenQueryData = {},
	dependencies,
	hasScheduleIssues,
	interactiveChartItems,
	isExportTable,
	issuesMap,
	items,
	onCreateClick,
	onItemUpdate,
	onJiraItemUpdate,
	setInfiniteScrollerRef,
	setSelectedIssueKey,
	updateIssueParent,
}: {
	childrenQueryData: ChildrenQueryData<TimelineViewIssuesReturn>;
	dependencies: StateDependencyItem[];
	hasScheduleIssues: boolean;
	interactiveChartItems: InteractiveChartItem[];
	isExportTable: boolean;
	issuesMap: Map<string, TimelineViewIssue>;
	items: Item[];
	onCreateClick: OnCreateClick;
	onItemUpdate: OnChartItemUpdate;
	onJiraItemUpdate: OnJiraItemUpdate;
	setInfiniteScrollerRef: (node: Element | null) => void;
	setSelectedIssueKey: (arg1: string) => void;
	updateIssueParent: OnItemDrop;
}) => {
	// do not initialise the renderers for SSR
	if (__SERVER__) {
		return {};
	}

	return {
		renderListItemMenuOption: (props: ListItemMenuOptionProps) => (
			<ListItemMenuOption
				{...props}
				updateIssueParent={updateIssueParent}
				onItemUpdate={onJiraItemUpdate}
			/>
		),
		renderListItemModal: (props: ListItemModalProps) => (
			<ListItemModal {...props} onItemUpdate={onJiraItemUpdate} />
		),
		// @ts-expect-error - TS7031 - Binding element 'id' implicitly has an 'any' type.
		renderListHeaderItem: ({ id }) => (id === DEFAULT_HEADER_ROW_ID ? <ListHeader /> : null),
		// @ts-expect-error - TS7006 - Parameter 'state' implicitly has an 'any' type.
		renderListItemContent: ({ id }: ListItemContentRendererProps, state) => {
			if (isItemSkeleton(id)) {
				return (
					// @ts-expect-error - TS2741 - Property 'backgroundColor' is missing in type '{}' but required in type 'Readonly<ThemedOuterStyledProps<ClassAttributes<HTMLDivElement> & HTMLAttributes<HTMLDivElement> & { backgroundColor: string; }, any>>'.
					<LoadingItem data-testid="business-timeline.ui.timeline-view.loading-icon" />
				);
			}
			const issue = issuesMap.get(id);
			if (!issue) {
				return null;
			}

			const { summary, ...issueProps } = issue;

			const isFirstItem = id === issuesMap.values().next().value?.issueId;
			const isLastItem = id === Array.from(issuesMap.values()).pop()?.issueId;

			if (state?.isVirtual) {
				return (
					<RoadmapListItemContentLite
						title={summary || ''}
						iconUrl={issue.issueType.iconUrl}
						itemKey={issue.key}
					/>
				);
			}

			return (
				<ListItemContent
					isFirstItem={isFirstItem}
					isLastItem={isLastItem}
					title={summary}
					issue={issueProps}
					setSelectedIssueKey={setSelectedIssueKey}
					onCreateClick={onCreateClick}
					setInfiniteScrollerRef={setInfiniteScrollerRef}
				/>
			);
		},
		renderListItemCreate: ({ anchorId, canCreateChildOverride }: ListItemCreateRendererProps) => {
			/*
			 * Renders an empty placeholder row after all other issue rows.
			 * This is a workaround to ensure that our custom sticky
			 * inline create component doesn't overlap the last
			 * issue in the table.
			 *
			 * Instead, the sticky inline create component will
			 * overlap the empty placeholder row.
			 */

			if (!anchorId && !canCreateChildOverride) {
				return null;
			}

			return (
				<TimelineTableInlineCreate
					childrenQueryData={childrenQueryData}
					issuesMap={issuesMap}
					items={items}
				/>
			);
		},
		renderChartItemContent: (
			{ id }: ChartItemContentRendererProps,
			state: ChartItemContentRendererState,
		) => {
			if (isItemSkeleton(id)) {
				return <ChartItemContentSkeleton />;
			}
			const issue = issuesMap.get(id);
			if (!issue) {
				return null;
			}

			const interactiveItem = interactiveChartItems.find((item) => item.id === id);
			if (!interactiveItem) {
				// interactiveItem will always be defined.
				return null;
			}

			if (state?.isVirtual) {
				return (
					<ChartItemContentLite
						issue={issue}
						startDate={interactiveItem.startDate}
						dueDate={interactiveItem.dueDate}
						level={interactiveItem.level}
					/>
				);
			}

			return (
				<ChartItemContent
					dueDate={interactiveItem.dueDate}
					isExportTable={isExportTable}
					isHovered={Boolean(state?.isHovered)}
					isReadOnly={!hasScheduleIssues}
					issue={issue}
					level={interactiveItem.level}
					onItemUpdate={onItemUpdate}
					placeholder={interactiveItem.placeholder}
					setSelectedIssueKey={setSelectedIssueKey}
					startDate={interactiveItem.startDate}
				/>
			);
		},
		renderChartTimelineOverlay: () => (
			<ChartTimelineOverlay issuesMap={issuesMap} dependencies={dependencies} />
		),
		...(!isExportTable
			? {
					// @ts-expect-error - TS7006 - Parameter 'props' implicitly has an 'any' type.
					renderProvider: (props) => <TableProvider {...props} items={interactiveChartItems} />,
					renderChartOverlay: () => <ChartOverlay issuesMap={issuesMap} />,
				}
			: {}),
	};
};

const useListWidth = () =>
	useState<number>(
		fg('project_timeline_multi-select_and_checkboxes')
			? DEFAULT_LIST_WIDTH()
			: DEFAULT_LIST_WIDTH() + 40,
	);

export const TimelineView = memo(() => {
	const { formatMessage } = useIntl();

	const pageHeading = formatMessage(messages.timelineHeading);

	const timelineRef = useRef<HTMLDivElement>(null);

	const [hierarchyState, hierarchyActions] = useHierarchyStore();

	const childrenQueryData = hierarchyState.childrenQueryData;

	const expandedItems = hierarchyState.expandedItems;

	const setChildrenQueryData = hierarchyActions.setChildrenQueryData;

	const setIssueExpanded = hierarchyActions.setIssueExpanded;

	const {
		data: { items, issuesMap, issueParentMap, dependencies, interactiveChartItems },
		error,
		loading,
		rankIssue,
		updateItemsInCache,
		loadNextPage,
		isLoadingNextPage,
		updateIssueParent,
		csvProps,
	} = useTimelineViewIssues({ childrenQueryData, expandedItems });

	const [{ shouldExpandAllItems }, { disableExpandCollapseAllItems }] = useExpandCollapseAllItems({
		view: 'timeline',
	});

	const listItemMenuOptions = useListItemMenuOptions();

	const { isFiltering } = useFilteredViewContextInput({ view: 'timeline' });
	const onItemDrop: OnItemDrop = useCallback(
		(id, payload, analyticsEvent) => {
			const { parentId, rankRequest } = payload;
			const isUpdateParent = 'parentId' in payload;
			if (isFiltering || (!isUpdateParent && rankRequest)) {
				return rankIssue(id, payload, analyticsEvent);
			}
			if (parentId && !expandedItems[parentId]) setIssueExpanded(parentId, true);
			return updateIssueParent(id, payload, analyticsEvent);
		},
		[expandedItems, isFiltering, rankIssue, setIssueExpanded, updateIssueParent],
	);

	const { loading: startDateFieldLoading } = useStartDateField();
	const viewMode = useViewMode();
	const isViewModeSideBar = viewMode === 'SIDEBAR';
	const [{ createItemAnchor }, { setCreateItemAnchor }] = useCreateItemAnchor();

	const {
		data: { issueTypes },
	} = useIssueTypesAndFields({ issueOperation: 'CREATE' });

	const { hasInlineCreate, hasCreateSubtask, featureView, hasScheduleIssues } =
		useTimelineFeatures();

	const interactionMetrics = useInteractionMetrics();

	const [{ timelineMode, today, timelineDuration, timelineOrigin }] = useTimelineUI();

	const [selectedIssueKey, setSelectedIssueKey] = useQueryParam(SELECTED_ISSUE_PARAM);

	const [listWidth, setListWidth] = useListWidth();

	const selectedItemIds = useMemo(() => {
		if (!selectedIssueKey) {
			return [];
		}

		const selectedIssueId = Array.from(issuesMap.values()).find(
			(issue) => issue.key === selectedIssueKey,
		)?.issueId;

		return selectedIssueId ? [selectedIssueId] : [];
		// Cannot use issuesMap in the dependency array because it remains stable
		// even when issuesMap changes.
		// This happens because issuesMap is mutated directly in toTimelineData().
		// Instead we use items as a proxy for issuesMap.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [items, selectedIssueKey]);

	const setRef = useIntersectionObserver(loadNextPage);
	const setInfiniteScrollerRef = useCallback(
		(element: Element | null) => setRef(element),
		[setRef],
	);

	const hasSubtasks = issueTypes.some((issueType) => issueType.hierarchyLevel === -1);

	const { onBentoChange, onBentoDelete } = useDependencyUpdater({
		childrenQueryData,
	});

	const { onItemUpdate } = useUpdateItem({
		issuesMap,
		updateItemsInCache,
		childrenQueryData,
	});

	const { onItemUpdate: onJiraItemUpdate } = useJiraUpdateItem({
		issuesMap,
		updateItemsInCache,
		childrenQueryData,
	});

	const onBentoClose = useCallback(() => {
		setSelectedIssueKey(undefined);
	}, [setSelectedIssueKey]);

	const onDeleteSuccess = useCallback(
		// This may show up as "unused ts-expect-error" locally, but fails in pipelines
		// @ts-expect-error - TS7031 -  Binding element 'issueId' implicitly has an 'any' type.
		({ issueId }) => {
			const parentId = issueParentMap.get(issueId);
			onBentoDelete({
				issueId: Number(issueId),
				parentId: Number(parentId),
			});
		},
		[onBentoDelete, issueParentMap],
	);

	// this needs to be memoised otherwise it re-renders when the table changes causing some elements to blink
	// a common reason for the table to change while this is open is the realtime implementation
	const bentoView = useMemo(
		() =>
			selectedIssueKey != null && (
				<ModalIssueApp
					issueKey={toIssueKey(selectedIssueKey)}
					onClose={onBentoClose}
					onChange={onBentoChange}
					onDeleteSuccess={onDeleteSuccess}
					analyticsSource={BUSINESS_TIMELINE}
				/>
			),
		[onBentoChange, selectedIssueKey, onBentoClose, onDeleteSuccess],
	);

	const onItemExpandChanged: OnItemExpandChanged = useCallback(
		(issueId, isExpanded) => {
			if (expandedItems[issueId] && shouldExpandAllItems) {
				disableExpandCollapseAllItems();
			}
			interactionMetrics[EXPAND_CHANGED].start();
			setIssueExpanded(issueId, !isExpanded);
		},
		[
			disableExpandCollapseAllItems,
			expandedItems,
			interactionMetrics,
			setIssueExpanded,
			shouldExpandAllItems,
		],
	);

	const onCreateClick: OnCreateClick = useCallback(
		(anchor) => {
			interactionMetrics[ADD_ITEM_CLICKED].start();
			if (anchor?.position === INSIDE && anchor?.parentId) {
				setIssueExpanded(anchor.parentId, true);
			}

			setCreateItemAnchor(anchor);
		},
		[interactionMetrics, setCreateItemAnchor, setIssueExpanded],
	);

	const itemsProp = useMemo(() => {
		/*
				We add a dummy {} item when the child inline create is open to replace the empty row otherwise rendered by the
				renderListItemCreate renderer. This addresses the bug reported in https://jbusiness.atlassian.net/browse/ICO-275
				*/
		const extendedItems =
			createItemAnchor != null
				? // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					[...items, {} as Item]
				: items;
		return loading ? LOADING_ITEMS : extendedItems;
	}, [createItemAnchor, items, loading]);

	const getRenderers = useCallback(
		({ isExportTable }: { isExportTable: boolean }) =>
			getTimelineRenderers({
				childrenQueryData,
				dependencies: loading ? [] : dependencies,
				hasScheduleIssues,
				interactiveChartItems,
				isExportTable,
				issuesMap,
				items,
				onCreateClick,
				onItemUpdate,
				onJiraItemUpdate,
				setInfiniteScrollerRef,
				setSelectedIssueKey,
				updateIssueParent,
			}),
		[
			childrenQueryData,
			loading,
			dependencies,
			hasScheduleIssues,
			interactiveChartItems,
			issuesMap,
			items,
			onCreateClick,
			onItemUpdate,
			onJiraItemUpdate,
			setInfiniteScrollerRef,
			setSelectedIssueKey,
			updateIssueParent,
		],
	);

	const [{ headerHeight }] = useHeaderState();
	const listHeight = getTimelineTableItemsHeight(itemsProp, expandedItems) + headerHeight;

	const exportButton = (
		<Export
			csvProps={csvProps}
			tableProps={{
				items: itemsProp,
				expandedItems,
				listWidth,
				listHeight,
				itemHeight: timelineProps.itemHeight,
				today,
				renderers: getRenderers({ isExportTable: true }),
			}}
		/>
	);

	const isReady = !loading && !error;

	const isCreateChildEnabled =
		!isFiltering && hasInlineCreate === true && hasSubtasks && hasCreateSubtask;

	let stickyInlineCreate: JSX.Element | undefined;
	if (hasInlineCreate) {
		stickyInlineCreate = (
			<StickyInlineCreate
				expandedItems={expandedItems}
				height={timelineProps.itemHeight}
				issuesMap={issuesMap}
				items={items}
				listWidth={listWidth}
				loading={loading}
				timelineRef={timelineRef.current}
			/>
		);
	}

	const content = (
		<>
			<UFOSegment name="business-timeline-table">
				<Table
					{...timelineProps}
					isDragEnabled={!startDateFieldLoading}
					createItemAnchor={createItemAnchor}
					DndLongTaskMetric={DndLongTaskMetric<TimelineTableInteractionMetricType>}
					expandedItems={expandedItems}
					isCreateChildEnabled={isCreateChildEnabled}
					// This needs to be enabled so that the custom sticky inline create has an extra row for scrolling
					// See: https://jbusiness.atlassian.net/browse/XPROJ-250
					isCreateLastEnabled={hasInlineCreate}
					isCreateSiblingEnabled={hasInlineCreate === true}
					isSelectEnabled
					isVirtualiseEnabled
					items={itemsProp}
					listWidth={listWidth}
					onCreateClick={onCreateClick}
					onItemDrop={onItemDrop}
					onItemExpandChanged={onItemExpandChanged}
					onSetListWidth={setListWidth}
					renderers={getRenderers({ isExportTable: false })}
					selectedItemIds={selectedItemIds}
					marshalOptions={marshalOptions}
					timelineDuration={timelineDuration}
					timelineMode={timelineMode}
					timelineOrigin={timelineOrigin}
					today={today}
					isTreeDrag
					isMeatballsButtonEnabled={fg('jsw_roadmaps_timeline_table_meatballs_menu_a11y')}
					{...(fg('jsw_roadmaps_timeline_table_meatballs_menu_a11y') && {
						listItemMenuOptions,
					})}
				/>
			</UFOSegment>
			<StickyInlineNudge
				height={timelineProps.itemHeight}
				isEmpty={items.length === 0}
				listWidth={listWidth}
				loading={loading}
				timelineRef={timelineRef.current}
			/>
			{stickyInlineCreate}
			<LoadingMoreSpinner
				isLoading={isLoadingNextPage}
				loadingMessage={formatMessage(messages.loadingMoreItems)}
			/>
		</>
	);

	return (
		<>
			{!isFiltering && (
				<ChildrenController
					expandedItemIssueIds={Object.keys(expandedItems)}
					setChildrenQueryData={setChildrenQueryData}
					ChildQueryController={TimelineViewChildQueryController}
				/>
			)}

			<UFOLoadHold name="timeline-view" hold={loading} />
			<RenderStartMark view={featureView} loading={loading} />
			<BusinessDocumentTitle pageHeading={pageHeading} />
			<Toolbar exportToImageButton={exportButton} />

			{isVisualRefreshEnabled() && fg('jira_nav4_beta_drop_1') ? (
				<TimelineContainer visualRefresh={fg('jira_nav4_beta_drop_2')} ref={timelineRef}>
					{content}
				</TimelineContainer>
			) : (
				<TimelineContainerOld ref={timelineRef}>{content}</TimelineContainerOld>
			)}
			<PerformanceAnalytics view={featureView} loading={loading} itemsCount={items.length} />
			{!loading && <JWMSpaStatePageReady />}
			<InteractionMetrics />
			<TimelineViewUpdater childrenQueryData={childrenQueryData} />
			{isViewModeSideBar ? (
				<SidebarIssueView
					issueKey={selectedIssueKey}
					onClose={onBentoClose}
					onChange={onBentoChange}
					onIssueDeleteSuccess={onDeleteSuccess}
					analyticsSource={BUSINESS_TIMELINE}
				/>
			) : (
				bentoView
			)}

			{isReady && <ExperienceSuccess experience={MAIN_EXPERIENCE} />}
			{error && <ExperienceFailed experience={MAIN_EXPERIENCE} error={error} />}
			<PrefetchIssueDataController />
			<ExpandCollapseAllController />
		</>
	);
});

// eslint-disable-next-line jira/import/no-anonymous-default-export
export default () => (
	<TimelineViewUpdaterProvider>
		<TimelineView />
	</TimelineViewUpdaterProvider>
);

// border-radius set on the TimelineContainer cuts off the timeline border, so this hack brings it back
const fakeBorder = {
	content: '',
	background: 'transparent',
	border: `1px solid ${token('color.border', colors.N40)}`,
	borderBottom: 'none',
	width: token('space.100', '8px'),
	height: token('space.100', '8px'),
	position: 'absolute',
	display: 'block',
	top: 0,
	zIndex: zIndex.HIGHEST,
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles
export const TimelineContainer = styled.div<{ visualRefresh?: boolean }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
	backgroundColor: token('elevation.surface', colors.N0),
	borderTopLeftRadius: token('border.radius.200', '8px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
	borderRight: `1px solid ${token('color.border.disabled', colors.N40)}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
	borderBottom: `1px solid ${token('color.border.disabled', colors.N40)}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
	color: token('color.text', colors.N800),
	flex: '1 1 auto',
	overflow: 'hidden',
	boxSizing: 'border-box',
	position: 'relative',
	padding: token('space.100', '8px'),

	/* top-left rounded corner */
	'&::after': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
		...fakeBorder,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
		clipPath: ({ visualRefresh }) =>
			visualRefresh ? `inset(0 round ${token('space.100')})` : undefined,
		borderRight: 'none',
		borderRadius: `${token('space.100', '8px')} 0 0`,
		left: 0,
	},

	/* We are hiding inline create in the bottom of timeline table using CSS
	This is a temporary fix until timeline-table supports more granular inline create booleans */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	"[data-testid='roadmap.timeline-table.main.list.create-bubble.button']": {
		display: 'none',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const TimelineContainerOld = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('elevation.surface', colors.N0),
	borderRadius: token('border.radius.200', '8px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	borderRight: `1px solid ${token('color.border.disabled', colors.N40)}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	borderBottom: `1px solid ${token('color.border.disabled', colors.N40)}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text', colors.N800),
	flex: '1 1 auto',
	overflow: 'hidden',
	boxSizing: 'border-box',
	position: 'relative',
	padding: token('space.100', '8px'),

	/* top-left rounded corner */
	'&::after': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		...fakeBorder,
		borderRight: 'none',
		borderRadius: `${token('space.100', '8px')} 0 0`,
		left: 0,
	},

	/* top-right rounded corner */
	'&::before': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		...fakeBorder,
		borderLeft: 'none',
		borderRadius: `0 ${token('space.100', '8px')} 0`,
		right: token('space.0', '0px'),
	},

	/* We are hiding inline create in the bottom of timeline table using CSS
		This is a temporary fix until timeline-table supports more granular inline create booleans */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	"[data-testid='roadmap.timeline-table.main.list.create-bubble.button']": {
		display: 'none',
	},
});
